import * as React from "react";

import { cn } from "@/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

export interface LargeReviewModalProps {
  /** Whether the modal is open */
  open: boolean;
  /** Callback when open state changes */
  onOpenChange: (open: boolean) => void;
  /** Modal title */
  title: string;
  /** Optional description */
  description?: string;
  /** Size variant */
  size?: "default" | "lg" | "xl" | "full";
  /** Optional header actions (rendered next to title) */
  headerActions?: React.ReactNode;
  /** Main content */
  children: React.ReactNode;
  /** Footer content with actions */
  footer?: React.ReactNode;
  /** Whether footer should be sticky at bottom */
  stickyFooter?: boolean;
  /** Whether clicking backdrop should close modal */
  closeOnBackdropClick?: boolean;
  /** Show close button */
  showCloseButton?: boolean;
  /** Additional className */
  className?: string;
}

const SIZE_CLASSES = {
  default: "max-w-lg",
  lg: "max-w-2xl",
  xl: "max-w-4xl",
  full: "max-w-[90vw] h-[90vh]",
};

export function LargeReviewModal({
  open,
  onOpenChange,
  title,
  description,
  size = "default",
  headerActions,
  children,
  footer,
  stickyFooter = false,
  closeOnBackdropClick = true,
  showCloseButton = true,
  className,
}: LargeReviewModalProps) {
  const sizeClass = SIZE_CLASSES[size];
  const isFullSize = size === "full";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          sizeClass,
          isFullSize && "h-[90vh] overflow-hidden",
          !isFullSize && "max-h-[85vh] overflow-hidden",
          className
        )}
        onInteractOutside={(e) => {
          if (!closeOnBackdropClick) {
            e.preventDefault();
          }
        }}
        showClose={showCloseButton}
      >
        {/* Sticky Header */}
        <DialogHeader className={cn("shrink-0 pb-4 border-b border-border/30", isFullSize && "bg-background")}>
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <DialogTitle className="text-lg font-semibold">
                {title}
              </DialogTitle>
              {description && (
                <DialogDescription className="text-sm text-muted-foreground mt-1">
                  {description}
                </DialogDescription>
              )}
            </div>
            {headerActions && (
              <div className="shrink-0 flex items-center gap-2">
                {headerActions}
              </div>
            )}
          </div>
        </DialogHeader>

        {/* Scrollable Content */}
        <div className={cn(
          "flex-1 overflow-y-auto py-4",
          !stickyFooter && "pb-6"
        )}>
          {children}
        </div>

        {/* Sticky Footer */}
        {footer && (
          <DialogFooter
            className={cn(
              "shrink-0 pt-4 border-t border-border/30",
              stickyFooter && "sticky bottom-0 bg-background"
            )}
          >
            {footer}
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}

/**
 * Convenience component for large review modals with common patterns
 */
export interface ReviewModalActions {
  /** Primary action label */
  primaryLabel?: string;
  /** Primary action handler */
  onPrimary?: () => void;
  /** Primary action disabled state */
  primaryDisabled?: boolean;
  /** Secondary action label */
  secondaryLabel?: string;
  /** Secondary action handler */
  onSecondary?: () => void;
  /** Show cancel button */
  showCancel?: boolean;
}

export interface ReviewModalProps extends Omit<LargeReviewModalProps, "footer"> {
  actions?: ReviewModalActions;
}

export function ReviewModal({
  title,
  description,
  open,
  onOpenChange,
  actions,
  children,
  ...dialogProps
}: ReviewModalProps) {
  const {
    primaryLabel = "Approve",
    onPrimary,
    primaryDisabled = false,
    secondaryLabel,
    onSecondary,
    showCancel = true,
  } = actions || {};

  return (
    <LargeReviewModal
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      description={description}
      footer={
        <div className="flex items-center justify-between w-full">
          <div>
            {showCancel && (
              <Button
                variant="ghost"
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
            )}
          </div>
          <div className="flex items-center gap-2">
            {secondaryLabel && (
              <Button
                variant="outline"
                onClick={onSecondary}
              >
                {secondaryLabel}
              </Button>
            )}
            {onPrimary && (
              <Button
                onClick={onPrimary}
                disabled={primaryDisabled}
              >
                {primaryLabel}
              </Button>
            )}
          </div>
        </div>
      }
      {...dialogProps}
    >
      {children}
    </LargeReviewModal>
  );
}
